// --- your imports remain same ---
import React, { useEffect, useState } from "react";
import "./SubCategories.css";
import Header from "../Layout/Header/Header";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getProductDetailsAPI } from "../../../Store/Action/productAction";
import ProductCard from "../CustomComponents/ProductCard";
import { getLoggedInUserDetailsAPI } from "../../../Store/Action/UserAction";
import CustomLoader from "../CustomComponents/CustomLoader/CustomLoader";
import OrderWeightInfoPopup from "../Auth/MyAccount/CustomModals/OrderWeightInfoPopup";
import Footer from "../Layout/Footer/Footer";
import { getCartTotalAPI } from "../../../Store/Action/cartAction";
import { Helmet } from "react-helmet-async";

const SubCategories = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [subCategories, setSubCategories] = useState([]);
  const [productsBySubCategory, setProductsBySubCategory] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [userRole, setUserRole] = useState("");

  const isWastageEnabled = useSelector(
    (state) => state.wastage.isWastageEnabled
  );

  // -------------------------------------------------------------------
  //  ⭐ HELPER FUNCTIONS TO REMOVE REPEATED LOGIC
  // -------------------------------------------------------------------
  const getSubCatId = (subCat) => {
    return location?.state?.fromSubCatDropdown ||
      location?.state?.fromHomePageCategory ||
      location?.state?.fromShopByCategoryViewAllPage
      ? subCat.product_category_id
      : subCat.product_category_id;
  };

  const getSubCatName = (subCat) => {
    return location?.state?.fromSubCatDropdown ||
      location?.state?.fromHomePageCategory ||
      location?.state?.fromShopByCategoryViewAllPage
      ? subCat.product_category_name
      : subCat.product_category_name;
  };

  // -------------------------------------------------------------------

  const appliedFilters = useSelector((state) => state.filter.appliedFilters);

  useEffect(() => {
    if (location?.state?.subCategories) {
      setSubCategories(location.state.subCategories);
    }
  }, [location.state]);

  useEffect(() => {
    dispatch(
      getLoggedInUserDetailsAPI({}, (result) => {
        setUserRole(result?.data?.[0]?.user_role_name);
      })
    );
  }, [dispatch]);

  // -------------------------------------------------------------------
  //  ⭐ MAIN FETCH LOGIC
  // -------------------------------------------------------------------
  useEffect(() => {
    setIsLoading(true);

    if (
      appliedFilters?.status &&
      appliedFilters?.screen_name === `${location?.pathname?.split("/")[1]}`
    ) {
      const updated = {};

      subCategories.forEach((subCat) => {
        const id = getSubCatId(subCat);
        updated[id] = { status: true, data: [] };
      });

      appliedFilters.data.forEach((filter) => {
        const id = filter.product_category_id;
        if (!updated[id]) updated[id] = { status: true, data: [] };
        updated[id].data.push(filter);
      });

      setProductsBySubCategory(updated);
      setIsLoading(false);
    } else {
      subCategories.forEach(async (subCat) => {
        const subCategoryId = getSubCatId(subCat);

        try {
          await dispatch(
            getProductDetailsAPI(
              {
                product_category_id: subCategoryId,
                manufacturer_id: location?.state?.manufacturerId,
              },
              (result) => {
                setProductsBySubCategory((prev) => ({
                  ...prev,
                  [subCategoryId]: result,
                }));
                setIsLoading(false);
              },
              {},
              location?.state?.manufacturerId
            )
          );
        } catch (error) {
          console.log("Error fetching products:", error);
        }
      });
    }
  }, [dispatch, subCategories, appliedFilters]);
  // -------------------------------------------------------------------

  const handleViewAllClick = (id, name) => {
    navigate(`/productsList/${id}`, {
      state: {
        subCategoryId: id,
        subCategoryName: name,
        manufacturerId: location?.state?.manufacturerId,
      },
    });
  };

  const cartChangeTrigger = useSelector(
    (state) => state.popupTrigger.cartChangeTrigger
  );

  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const handleClosePopup = () => setIsPopupVisible(false);

  useEffect(() => {
    dispatch(
      getCartTotalAPI({}, (result) => {
        if (result?.status && result?.data?.length > 0) {
          const cart = result.data[0];
          const hasItems =
            Object.values(cart.product_total).some(
              (item) => item.quantity > 0
            ) ||
            Object.values(cart.per_pc_product_total).some(
              (item) => item.quantity > 0
            );
          setIsPopupVisible(hasItems);
        }
      })
    );
  }, [dispatch, cartChangeTrigger]);

  const refreshSubCategoryProducts = (subCategoryId) => {
    setIsLoading(true);

    dispatch(
      getProductDetailsAPI(
        {
          product_category_id: subCategoryId,
          manufacturer_id: location?.state?.manufacturerId,
        },
        (result) => {
          setProductsBySubCategory((prev) => ({
            ...prev,
            [subCategoryId]: result,
          }));
          setIsLoading(false);
        },
        {},
        location?.state?.manufacturerId
      )
    );
  };

  // -------------------------------------------------------------------
  //  ⭐ UI
  // -------------------------------------------------------------------
  return (
    <>
      <Helmet>
        <title>
          {subCategories[0]?.product_category_name || "Products"} - Buy Online |
          Zabali
        </title>
        <meta
          name="description"
          content={`Shop top products in ${subCategories[0]?.product_category_name}. Browse products, prices, and offers online.`}
        />
      </Helmet>

      <Header />

      {isLoading ? (
        <div className="loaderContainer">
          <CustomLoader />
        </div>
      ) : (
        <>
          <div
            className="hrLineDiv"
            style={{ height: "1px", backgroundColor: "#727272" }}
          />

          <div className="subCategoriesMainDiv">
            {subCategories?.map((subCat, index) => {
              const subCatId = getSubCatId(subCat);
              const subCatName = getSubCatName(subCat);

              return (
                <div key={subCatId}>
                  <div className="upperContent">
                    <h1>{subCatName || "Subcategory name"}</h1>
                    <p>Shop our most popular products, loved by customers</p>
                  </div>

                  <div className="lowerContent">
                    <div className="hrLine" />

                    {/* View All Button */}
                    <div className="view-all">
                      {productsBySubCategory[subCatId]?.data?.length > 0 && (
                        <p
                          onClick={() =>
                            handleViewAllClick(subCatId, subCatName)
                          }
                        >
                          View all
                        </p>
                      )}
                    </div>

                    <div className="cardsMainDiv">
                      {productsBySubCategory[subCatId]?.data?.length > 0 ? (
                        productsBySubCategory[subCatId].data
                          ?.slice(0, 4)
                          ?.map((prod, index) => (
                            <ProductCard
                              key={index}
                              image={prod?.product_image?.product_image_url}
                              title={
                                prod?.product_subCategory_name
                                  ? `${prod.product_subCategory_name} (${prod.stamp_purity_value}, ${prod.stamp_melting_purity_value})`
                                  : prod.product_name
                              }
                              netWeight={prod.product_net_weight}
                              wastage={prod.product_wastage}
                              fine={
                                userRole.toLowerCase() === "retailer"
                                  ? prod.product_retailer_per_pcs_fine
                                  : userRole.toLowerCase() === "wholesaler"
                                    ? prod.product_whole_seller_per_pcs_fine
                                    : "N/A"
                              }
                              product={prod}
                              isWishlist={prod.wishlist_id}
                              flagCart={prod.flag_cart}
                              userRole={userRole}
                              isWastageEnabled={isWastageEnabled}
                              // onGetWishlistProduct={refreshSubCategoryProducts}
                              onGetWishlistProduct={(pageNumber) => {
                                refreshSubCategoryProducts(subCatId);
                              }}
                            />
                          ))
                      ) : (
                        <p>No products available in this category.</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {isPopupVisible && (
            <OrderWeightInfoPopup
              onClose={handleClosePopup}
              refreshTrigger={cartChangeTrigger}
            />
          )}
        </>
      )}

      <Footer />
    </>
  );
};

export default SubCategories;
